import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Skeleton, Typography } from "@mui/material";
import AppState from "../../store/AppState";
import { selectIsModalOpen } from "../../store/modal/ModalSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import {
  styleClasses,
  StyledDiv,
  StyledtextSelect,
  StyledtextUnSelect,
} from "./RemoveProductsModalStyles";
import RemoveProductCategorySelection from "../remove-products-modal/RemoveProductCategorySelection";
import {
  DeleteProductByCategories,
  ResetProductDeletedState,
  loadCategoriesByProductsId,
} from "../../store/remove-product-categories/RemoveProductCategoriesActions";
import {
  selectIsCategoriesLoading,
  selectIsProductDeleted,
  selectProductCategories,
  selectIsProgressStopped,
} from "../../store/remove-product-categories/RemoveProductCategoriesSelector";
import { selectCurrentCategory } from "../../store/category/CategorySelectors";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "../../store/store-list/StoreListSelectors";
import { useIntl } from "react-intl";
import ConfirmRemoveProducts from "./ConfirmRemoveProducts";
import { CircularProgress } from "@material-ui/core";
import CustomDialog from "../common/DialogContainer";
import ButtonComponent from "../common/ButtonComponent";
import { selectValidProductIds } from "store/clipboard/ClipBoardSelectors";
import { selectLockedProducts } from "store/product-list/ProductListSelectors";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import { filterLockedProducts } from "utils/ProductUtil";
import { AdapterInfoObject } from "utils/AppConfigUtils";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";

interface CategoryList {
  name: string;
  categoryId: string;
  isAutomatedCollection?: boolean;
}

const RemoveProductsModal = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [selectedIds, setSelectedIds] = useState([] as string[]);
  const [categoryList, setCategoryList] = useState([] as CategoryList[]);
  const productCategories = useSelector(selectProductCategories);
  const productIds = useSelector(selectValidProductIds);
  const isCategoriesLoading = useSelector(selectIsCategoriesLoading);
  const isProductDeleted = useSelector(selectIsProductDeleted);
  const currentCategoryId = useSelector(selectCurrentCategory);
  const catalogId = useSelector(selectCurrentCatalogId);
  const isProgressStopped = useSelector(selectIsProgressStopped);
  const lockedProductsInCategory = useSelector((state: AppState) =>
    selectLockedProducts(state, currentCategoryId, catalogId),
  );
  const [loader, setLoader] = useState(false);
  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, "RemoveProductsModal"),
  );
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const unmanagedSupported = adapterInfo?.capabilities?.includes(
    "unmanaged-products",
  ) as boolean;
  const currentStoreId = useSelector(selectCurrentStoreId);
  const currentLocale = useSelector(selectCurrentLocale);
  const handleClose = () => {
    dispatch(setModalState("RemoveProductsModal", false));
    //dispatch(CloseModal({}));
  };

  useEffect(() => {
    if (isProductDeleted) {
      handleClose();
      dispatch(ResetProductDeletedState());
      setLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductDeleted]);

  useEffect(() => {
    if (isModalOpen && productIds.length > 0) {
      dispatch(
        loadCategoriesByProductsId({
          storeId: currentStoreId ?? undefined,
          catalogId: catalogId ?? undefined,
          productIds,
          localeCode: currentLocale ?? undefined,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, productIds, dispatch]);

  useEffect(() => {
    setSelectedIds([]);
    const categoriesList: CategoryList[] = [];
    productCategories.forEach((prod) => {
      prod.categories.forEach((cat) =>
        categoriesList.push({
          name: `${cat.name} [${cat.code}]`,
          categoryId: cat.categoryId,
          isAutomatedCollection: cat?.isAutomatedCollection ?? false,
        }),
      );
    });
    const uniqueCat = [
      ...new Map(categoriesList.map((cat) => [cat["name"], cat])).values(),
    ];
    setCategoryList(uniqueCat);
    if (uniqueCat.find((cat) => cat.categoryId === currentCategoryId)) {
      setSelectedIds([currentCategoryId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCategories]);

  const handleDelete = () => {
    const lockedProducts = filterLockedProducts(
      lockedProductsInCategory,
      productIds,
    );
    setLoader(true);
    dispatch(
      DeleteProductByCategories({
        categoryIds: selectedIds,
        productIds: productIds,
        lockedProducts: lockedProducts,
        unmanagedSupported: unmanagedSupported ? "unmanaged" : "manual",
      }),
    );
    dispatch(setModalState("ConfirmRemoveProducts", false));
  };

  const handleSelectAll = () => {
    const mapIds = {};
    selectedIds.forEach((id) => (mapIds[id] = true));
    const finalData = [...selectedIds];
    categoryList.forEach((cat) => {
      if (!mapIds[cat.categoryId] && !cat?.isAutomatedCollection) {
        finalData.push(cat.categoryId);
      }
    });
    setSelectedIds(finalData);
  };

  const handleUnselectAll = () => {
    setSelectedIds([]);
  };

  const removeProductsModalTitle = () => {
    return (
      <Typography variant="inherit">
        {intl.formatMessage({
          id: "removeProductsModal.modalTitle",
          defaultMessage: "Remove from following categories",
        })}
      </Typography>
    );
  };

  const removeProductsModalContent = () => {
    return (
      <>
        <StyledDiv className={styleClasses.options}>
          {/* <Grid container direction="row" justify="flex-end" alignItems="center"> */}
          <StyledtextSelect
            className={styleClasses.spanSelect}
            onClick={handleSelectAll}
          >
            {intl.formatMessage({
              id: "general.selectAllLabel",
              defaultMessage: "Select All",
            })}
          </StyledtextSelect>
          /
          <StyledtextUnSelect
            className={styleClasses.spanUnselect}
            onClick={handleUnselectAll}
          >
            {intl.formatMessage({
              id: "general.unSelectAllLabel",
              defaultMessage: "Unselect All",
            })}
          </StyledtextUnSelect>
        </StyledDiv>
        <Typography variant="subHeader">
          {intl.formatMessage({
            id: "removeProductsModal.instruction",
            defaultMessage:
              "Selected products will be removed from the following checked categories:",
          })}
        </Typography>
        {isCategoriesLoading && categoryList.length === 0 ? (
          <StyledDiv
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "16px 0",
            }}
          >
            <Skeleton
                variant="rectangular"
                width={"40%"}
                height={15}
                animation="wave"
              />
          </StyledDiv>
        ) : (
          <RemoveProductCategorySelection
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            categoryList={categoryList}
          />
        )}
      </>
    );
  };

  const dialogActions = () => {
    return (
      <StyledDiv className={styleClasses.actiondiv}>
        <ButtonComponent
          color="secondaryButtonColorCTABlue"
          variant="outlined"
          onClick={handleClose}
        >
          {intl.formatMessage({
            id: "general.cancel",
            defaultMessage: "Cancel",
          })}
        </ButtonComponent>
        <ButtonComponent
          color="primary"
          variant="contained"
          justifyContent="center"
          onClick={() => dispatch(setModalState("ConfirmRemoveProducts", true))}
        >
          {intl.formatMessage({
            id: "general.save",
            defaultMessage: "Save",
          })}
        </ButtonComponent>
      </StyledDiv>
    );
  };

  return (
    <>
      {loader && !isProgressStopped && (
        <StyledDiv className={styleClasses.overlay}>
          <CircularProgress />
        </StyledDiv>
      )}
      <CustomDialog
        open={isModalOpen}
        onClose={() => handleClose()}
        title={removeProductsModalTitle()}
        fullWidth
        maxWidth={"md"}
        actions={dialogActions()}
      >
        <>
          {removeProductsModalContent()}
          <ConfirmRemoveProducts
            handleDelete={handleDelete}
            handleClose={handleClose}
          />
        </>
      </CustomDialog>
    </>
  );
};

export default RemoveProductsModal;
